/**
 * Playlist drawer — left rail of the FULL ("Content Viewer") layout.
 * Lists the agent's playlist items as cards (thumbnail + name + type +
 * duration), with an active highlight on the currently-open content.
 * Verbatim port of legacy `PlaylistDrawer.vue`.
 *
 * Third leaf of the incremental Preact migration. Same controller
 * shape (createPlaylistDrawer → {el, setPlaylist, setSelected,
 * onSelect}) so ui.tsx stays unchanged; internals are signals-backed
 * Preact. Shares thumbnail icons + type labels with RecommendationCards
 * via `content-thumbnail.tsx`.
 */

import { signal } from '@preact/signals';
import { useState } from 'preact/hooks';
import { mountPreactIsland } from '../voice/preact-mount';
import { ThumbnailIcon, typeLabelFor } from './content-thumbnail';

export interface PlaylistItem {
  content_id: string;
  content_type: string;
  content_extension: string;
  content_public_id?: string;
  name: string;
  image?: string | null;
  url?: string | null;
  /**
   * Pre-resolved iframe-ready URL when the item is a remote-video
   * wrapper (vimeo, youtube, wistia, loom, vidyard, brightcove). When
   * set, the content panel uses this directly. Server-side
   * `parseRemoteVideo()` does the detection.
   */
  embed_url?: string | null;
  duration?: number;
  duration_seconds?: number;
}

export interface PlaylistDrawerController {
  el: HTMLElement;
  setPlaylist(items: PlaylistItem[], opts?: { loading?: boolean }): void;
  setSelected(it: PlaylistItem | null): void;
  onSelect(handler: (it: PlaylistItem) => void): void;
}

function formatDurationSecs(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (m && s) return `${m}min ${s}sec`;
  if (m) return `${m}min`;
  return `${s}sec`;
}

function itemDuration(it: PlaylistItem): string | null {
  const sec = it.duration ?? it.duration_seconds ?? 0;
  if (!sec) return null;
  return formatDurationSecs(sec);
}

function totalDuration(items: PlaylistItem[]): string {
  if (!items.length) return '0min 0sec';
  const total = items.reduce((sum, it) => {
    const v = it.duration ?? it.duration_seconds;
    return sum + (typeof v === 'number' ? v : 0);
  }, 0);
  return formatDurationSecs(total);
}

function PlaylistHeader({ items }: { items: PlaylistItem[] }) {
  const count = items.length;
  return (
    <div class="sp-playlist-header">
      <h6>My Playlist</h6>
      <div class="sp-playlist-info">
        <span>
          {count} {count === 1 ? 'topic' : 'topics'}
        </span>
        {' · '}
        <span>{totalDuration(items)}</span>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div class="sp-playlist-loading">
      <div class="sp-playlist-skeleton" />
      <div class="sp-playlist-skeleton" />
      <div class="sp-playlist-skeleton" />
    </div>
  );
}

function EmptyState() {
  return (
    <div class="sp-playlist-empty">
      <svg
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M9 2H15M15 2V6M15 2L19 6M9 2V6M9 2L5 6M5 6V20C5 21.1046 5.89543 22 7 22H17C18.1046 22 19 21.1046 19 20V6M5 6H19M9 11H15M9 15H15" />
      </svg>
      <p>No content available</p>
      <span class="sp-playlist-empty-hint">Content will appear here when available</span>
    </div>
  );
}

function PlaylistRow({
  item,
  active,
  onSelect,
}: {
  item: PlaylistItem;
  active: boolean;
  onSelect: () => void;
}) {
  // Local state for thumbnail-load failure so a 404 from a CDN with
  // expiring tokens (vimeo) doesn't leave a broken-image glyph in
  // the slot. Same pattern as RecommendationCards.
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = !!item.image && !imageFailed;
  const dur = itemDuration(item);
  const label = typeLabelFor(item.content_extension, item.content_type);

  return (
    <li
      class={`sp-playlist-item${active ? ' sp-playlist-item--active' : ''}`}
      onClick={onSelect}
    >
      <div class="sp-playlist-thumb">
        {showImage ? (
          <img
            src={item.image ?? ''}
            alt={item.name}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <ThumbnailIcon ext={item.content_extension} />
        )}
      </div>
      <div class="sp-playlist-info">
        <h6 class="sp-playlist-name" title={item.name}>
          {item.name}
        </h6>
        <span class="sp-playlist-type">
          {label}
          {dur ? ` · ${dur}` : ''}
        </span>
      </div>
    </li>
  );
}

function isSameItem(a: PlaylistItem | null, b: PlaylistItem | null): boolean {
  return (
    !!a &&
    !!b &&
    a.content_id === b.content_id &&
    a.content_type === b.content_type
  );
}

function PlaylistDrawerView({
  items,
  loading,
  selected,
  onSelect,
}: {
  items: PlaylistItem[];
  loading: boolean;
  selected: PlaylistItem | null;
  onSelect: (it: PlaylistItem) => void;
}) {
  let body;
  if (loading) {
    body = <LoadingSkeleton />;
  } else if (items.length === 0) {
    body = <EmptyState />;
  } else {
    body = (
      <ul class="sp-playlist-items">
        {items.map((it) => (
          <PlaylistRow
            key={`${it.content_id}-${it.content_type}`}
            item={it}
            active={isSameItem(it, selected)}
            onSelect={() => onSelect(it)}
          />
        ))}
      </ul>
    );
  }
  return (
    <div class="sp-playlist-content">
      <PlaylistHeader items={items} />
      {body}
    </div>
  );
}

/**
 * Signals-backed adapter so callers keep the imperative
 * `controller.setPlaylist(...)` style while rendering goes through
 * Preact. Mirrors the same pattern as `createContentPanel`.
 */
export function createPlaylistDrawer(): PlaylistDrawerController {
  const root = document.createElement('div');
  root.className = 'sp-playlist-drawer';

  const items = signal<PlaylistItem[]>([]);
  const loading = signal<boolean>(false);
  const selected = signal<PlaylistItem | null>(null);
  // Handler ref stays mutable so caller can swap the listener without
  // forcing a re-render. The Preact tree reads it through a stable
  // arrow that defers to the latest ref.
  let selectHandler: ((it: PlaylistItem) => void) | null = null;

  function Mounted() {
    return (
      <PlaylistDrawerView
        items={items.value}
        loading={loading.value}
        selected={selected.value}
        onSelect={(it) => selectHandler?.(it)}
      />
    );
  }
  mountPreactIsland(root, <Mounted />);

  return {
    el: root,
    setPlaylist(next, opts) {
      items.value = next;
      loading.value = !!opts?.loading;
    },
    setSelected(next) {
      selected.value = next;
    },
    onSelect(h) {
      selectHandler = h;
    },
  };
}
